import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

/**
 * Re-usable legal page wrapper. Sets per-page <title> + description and renders
 * a clean centered typographic layout that matches the rest of the site.
 *
 * NOTE: This file exports three components — one per legal page — so all of them
 * share the same skeleton and styling without code duplication.
 */
const LegalLayout = ({ title, description, eyebrow, children, testid }) => {
  useEffect(() => {
    document.title = `${title} · NIVARA`;
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    if (description) setMeta("description", description);
  }, [title, description]);

  return (
    <div className="rs-app min-h-screen bg-white" data-testid={testid}>
      <Header />
      <main className="max-w-3xl mx-auto px-6 lg:px-10 py-14 sm:py-20">
        <Link
          to="/"
          className="text-xs uppercase tracking-[0.2em] text-[var(--rs-text-muted)] hover:text-[var(--rs-primary)]"
          data-testid="legal-back-home"
        >
          ← Zurück zur Startseite
        </Link>
        <div className="rs-overline mt-8">{eyebrow}</div>
        <h1 className="font-[Chivo] text-4xl sm:text-5xl font-black tracking-tight mt-3 leading-[1]">
          {title}
        </h1>
        <div className="prose prose-sm sm:prose max-w-none mt-10 text-[var(--rs-text)] leading-relaxed">
          {children}
        </div>
        <p className="mt-14 text-xs text-[var(--rs-text-muted)] uppercase tracking-[0.2em]">
          Stand: {new Date().toLocaleDateString("de-CH", { day: "2-digit", month: "long", year: "numeric" })}
        </p>
      </main>
      <Footer />
    </div>
  );
};

// Re-usable typographic helpers — keep markup short and consistent
const H2 = ({ children }) => (
  <h2 className="font-[Chivo] text-xl sm:text-2xl font-black mt-10 mb-3">{children}</h2>
);
const H3 = ({ children }) => (
  <h3 className="font-[Chivo] text-base font-bold mt-6 mb-2 uppercase tracking-wide">{children}</h3>
);
const P = ({ children }) => <p className="my-3 text-[15px] leading-7">{children}</p>;
const UL = ({ children }) => <ul className="list-disc pl-5 my-3 space-y-1 text-[15px]">{children}</ul>;
const PH = ({ children }) => (
  <span className="bg-yellow-100 text-yellow-900 px-1.5 py-0.5 text-[13px] font-mono">{children}</span>
);

// ─────────────────────────────────────────────────────────────
//  AGB
// ─────────────────────────────────────────────────────────────
export const AGBPage = () => (
  <LegalLayout
    title="Allgemeine Geschäftsbedingungen"
    eyebrow="AGB"
    description="Allgemeine Geschäftsbedingungen der NIVARA Group für die Vermietung von Transportern (Mercedes Vito und Renault Master) in der Schweiz."
    testid="agb-page"
  >
    <P>
      Die folgenden Allgemeinen Geschäftsbedingungen ("AGB") regeln das Vertragsverhältnis zwischen
      der <strong>NIVARA Group, Weidstrasse 5, 9552 Bronschhofen</strong> ("Vermieterin",
      nachfolgend "NIVARA") und der Person,
      welche ein Fahrzeug mietet ("Mieter:in"). Mit der Buchung erklären Sie sich mit diesen AGB
      einverstanden.
    </P>

    <H2>1. Vertragsabschluss</H2>
    <P>
      Der Mietvertrag kommt durch die Bestätigung der Online-Buchung sowie die Unterzeichnung des
      digitalen Mietvertrags zustande. Die Mindestmietdauer beträgt für den Renault Master 3 Stunden
      und für den Mercedes Vito 1 Tag (24 Stunden).
    </P>

    <H2>2. Voraussetzungen Mieter:in</H2>
    <UL>
      <li>Mindestalter 21 Jahre (Vito) bzw. 20 Jahre (Master)</li>
      <li>Gültiger Führerausweis Kategorie B seit mindestens 2 Jahren</li>
      <li>Gültiger amtlicher Lichtbildausweis (ID/Pass)</li>
      <li>Bei Online-Buchung: gültige E-Mail-Adresse und Telefonnummer</li>
    </UL>

    <H2>3. Mietpreise &amp; Zahlung</H2>
    <P>
      Sämtliche Preise verstehen sich in Schweizer Franken (CHF) inklusive MwSt. Der vereinbarte
      Mietpreis ist bei Fahrzeugübergabe per TWINT oder in bar zu entrichten. Zusätzlich wird eine
      Kaution von CHF 300.– erhoben, welche bei ordnungsgemässer Rückgabe vollumfänglich zurückerstattet wird.
    </P>
    <H3>3.1 Aktionspreise</H3>
    <P>
      Aktionspreise (z. B. "Vito 3 Tage CHF 500.–") gelten nur für Buchungen innerhalb der
      Schweiz, sind nicht mit anderen Rabatten kumulierbar und können von NIVARA jederzeit
      angepasst oder beendet werden.
    </P>

    <H2>4. Versicherung &amp; Selbstbehalt</H2>
    <P>
      Sämtliche Fahrzeuge sind vollkaskoversichert. Der Selbstbehalt beträgt im Schadenfall
      CHF 1'000.– pro Ereignis. Vorsatz, Grobfahrlässigkeit, Fahren unter Alkohol- oder
      Drogeneinfluss sowie nicht autorisierte Personen am Steuer führen zum Verlust des
      Versicherungsschutzes — der gesamte Schaden geht zulasten der Mieter:in.
    </P>

    <H2>5. Übergabe &amp; Rückgabe</H2>
    <UL>
      <li>Fahrzeug wird vollgetankt übergeben und ist vollgetankt zurückzubringen</li>
      <li>Verspätete Rückgabe: CHF 25.– pro angefangene Stunde (Master) bzw. ein zusätzlicher Tagessatz (Vito)</li>
      <li>Zustand wird beidseits digital dokumentiert (Foto, Kilometerstand, Mängelnotiz)</li>
      <li>Reinigungspauschale bei stark verschmutzter Rückgabe: ab CHF 80.–</li>
    </UL>

    <H2>6. Kilometerpauschale</H2>
    <P>
      Der Mercedes Vito wird ohne Kilometerlimit vermietet. Beim Renault Master sind je nach
      gewähltem Paket 150–300 km inkludiert; jeder zusätzliche Kilometer wird mit CHF 0.35 verrechnet.
    </P>

    <H2>7. Nutzungseinschränkungen</H2>
    <P>Folgende Nutzungen sind ohne ausdrückliche Bewilligung untersagt:</P>
    <UL>
      <li>Untervermietung oder Weitergabe an Dritte</li>
      <li>Personentransport gegen Entgelt (Taxi-/Uber-Betrieb)</li>
      <li>Rennen, Geländefahrten, Fahrschulen</li>
      <li>Transport von Gefahrengut, gestohlenen oder illegalen Waren</li>
      <li>Fahrten ausserhalb der bei Buchung angegebenen Region (CH / EU)</li>
    </UL>

    <H2>8. Stornierung</H2>
    <UL>
      <li>Mehr als 72 Stunden vor Mietbeginn: kostenfrei</li>
      <li>24–72 Stunden vor Mietbeginn: 50 % des Mietpreises</li>
      <li>Weniger als 24 Stunden vor Mietbeginn / No-Show: 100 % des Mietpreises</li>
    </UL>

    <H2>9. Haftung der Mieter:in</H2>
    <P>
      Die Mieter:in haftet für sämtliche Schäden, Bussen, Gebühren und Strafen, welche während
      der Mietdauer am Fahrzeug oder durch dessen Benutzung entstehen, einschliesslich
      Park-, Geschwindigkeits- und Maut-Bussen, auch wenn diese erst nach Vertragsende
      bei NIVARA eintreffen.
    </P>

    <H2>10. Datenschutz</H2>
    <P>
      Die Datenerhebung und -bearbeitung erfolgt gemäss unserer{" "}
      <Link to="/datenschutz" className="text-[var(--rs-primary)] underline">
        Datenschutzerklärung
      </Link>.
    </P>

    <H2>11. Anwendbares Recht &amp; Gerichtsstand</H2>
    <P>
      Es gilt ausschliesslich Schweizer Recht unter Ausschluss des UN-Kaufrechts. Gerichtsstand ist{" "}
      <strong>St. Gallen</strong>.
    </P>
  </LegalLayout>
);

// ─────────────────────────────────────────────────────────────
//  Datenschutz
// ─────────────────────────────────────────────────────────────
export const PrivacyPage = () => (
  <LegalLayout
    title="Datenschutzerklärung"
    eyebrow="Datenschutz"
    description="Datenschutzerklärung von NIVARA Group gemäss revidiertem Schweizer Datenschutzgesetz (revDSG) und DSGVO."
    testid="privacy-page"
  >
    <P>
      NIVARA legt Wert auf den Schutz Ihrer persönlichen Daten. Diese Erklärung informiert Sie über
      Art, Umfang und Zweck der Datenbearbeitung gemäss revidiertem Schweizer Datenschutzgesetz
      (revDSG) sowie — soweit anwendbar — der EU-Datenschutz-Grundverordnung (DSGVO).
    </P>

    <H2>1. Verantwortliche Stelle</H2>
    <P>
      <strong>NIVARA Group</strong><br />
      Weidstrasse 5<br />
      9552 Bronschhofen, Schweiz<br />
      E-Mail: <a href="mailto:main@nivaragroup.ch" className="text-[var(--rs-primary)] underline">main@nivaragroup.ch</a><br />
      Telefon: <a href="tel:+41793966660" className="text-[var(--rs-primary)] underline">+41 79 396 66 60</a>
    </P>

    <H2>2. Welche Daten werden bearbeitet?</H2>
    <UL>
      <li><strong>Kontaktdaten:</strong> Name, Adresse, E-Mail, Telefonnummer</li>
      <li><strong>Identifikationsdaten:</strong> Führerausweisnummer, Geburtsdatum, Ausweisbild (für die Vertragsabwicklung)</li>
      <li><strong>Buchungsdaten:</strong> Fahrzeug, Mietzeitraum, Standort, Preis, Zahlungsart</li>
      <li><strong>Technische Daten:</strong> IP-Adresse, Browsertyp, Spracheinstellung (anonymisiert)</li>
    </UL>

    <H2>3. Zweck der Bearbeitung</H2>
    <UL>
      <li>Abwicklung des Mietvertrags (Erstellung, Übergabe, Rückgabe, Rechnungsstellung)</li>
      <li>Erfüllung gesetzlicher Aufbewahrungspflichten (10 Jahre gemäss OR)</li>
      <li>Kommunikation zur Buchung (E-Mail-Bestätigung, Rückrufanfragen)</li>
      <li>Verbesserung der Website (Webanalyse, anonymisiert)</li>
    </UL>

    <H2>4. Weitergabe an Dritte</H2>
    <P>
      Eine Weitergabe Ihrer Daten erfolgt ausschliesslich an folgende Auftragsverarbeiter, welche
      vertraglich auf den Datenschutz verpflichtet sind:
    </P>
    <UL>
      <li><strong>Resend Inc.</strong> — Versand transaktionaler E-Mails (Serverstandort EU)</li>
      <li><strong>MongoDB Atlas</strong> — Speicherung der Buchungsdaten (Serverstandort EU)</li>
      <li><strong>Strafverfolgungsbehörden</strong> — nur bei richterlicher Anordnung</li>
    </UL>

    <H2>5. Speicherdauer</H2>
    <P>
      Vertragsdaten werden während 10 Jahren gemäss Art. 958f OR aufbewahrt. Identifikationsdaten
      (Führerausweis-Bild) werden 24 Monate nach Vertragsende anonymisiert.
    </P>

    <H2>6. Ihre Rechte</H2>
    <UL>
      <li>Recht auf Auskunft über gespeicherte Daten</li>
      <li>Recht auf Berichtigung unrichtiger Daten</li>
      <li>Recht auf Löschung (sofern keine Aufbewahrungspflicht entgegensteht)</li>
      <li>Recht auf Einschränkung der Bearbeitung</li>
      <li>Recht auf Datenübertragbarkeit</li>
      <li>Beschwerderecht beim Eidgenössischen Datenschutz- und Öffentlichkeitsbeauftragten (EDÖB)</li>
    </UL>
    <P>
      Anfragen richten Sie bitte an{" "}
      <a href="mailto:main@nivaragroup.ch" className="text-[var(--rs-primary)] underline">main@nivaragroup.ch</a>.
    </P>

    <H2>7. Cookies &amp; LocalStorage</H2>
    <P>
      Diese Website verwendet ausschliesslich technisch notwendige Cookies und LocalStorage-Einträge
      (z. B. zur Speicherung der Spracheinstellung und der Anmeldung). Es werden keine Tracking-,
      Werbe- oder Drittanbieter-Cookies eingesetzt.
    </P>

    <H2>8. Änderungen dieser Erklärung</H2>
    <P>
      NIVARA behält sich vor, diese Datenschutzerklärung anzupassen, um gesetzlichen oder
      betrieblichen Änderungen Rechnung zu tragen. Die jeweils aktuelle Version ist auf{" "}
      <a href="https://nivaragroup.ch/datenschutz" className="text-[var(--rs-primary)] underline">nivaragroup.ch/datenschutz</a>{" "}
      abrufbar.
    </P>
  </LegalLayout>
);

// ─────────────────────────────────────────────────────────────
//  Impressum
// ─────────────────────────────────────────────────────────────
export const ImprintPage = () => (
  <LegalLayout
    title="Impressum"
    eyebrow="Impressum"
    description="Impressum und rechtliche Hinweise der NIVARA Group, Schweizer Transporter-Vermietung."
    testid="imprint-page"
  >
    <H2>Anbieter</H2>
    <P>
      <strong>NIVARA Group</strong><br />
      Weidstrasse 5<br />
      9552 Bronschhofen, Schweiz
    </P>

    <H2>Kontakt</H2>
    <P>
      Telefon: <a href="tel:+41793966660" className="text-[var(--rs-primary)] underline">+41 79 396 66 60</a><br />
      E-Mail: <a href="mailto:main@nivaragroup.ch" className="text-[var(--rs-primary)] underline">main@nivaragroup.ch</a><br />
      Website: <a href="https://nivaragroup.ch" className="text-[var(--rs-primary)] underline">nivaragroup.ch</a>
    </P>

    <H2>Handelsregister</H2>
    <P>
      Eingetragen im Handelsregister des Kantons <strong>St. Gallen</strong><br />
      UID-Nummer: <PH>[CHE-XXX.XXX.XXX]</PH><br />
      MwSt-Nr.: <PH>[CHE-XXX.XXX.XXX MWST]</PH>
    </P>

    <H2>Vertretungsberechtigte Person</H2>
    <P>
      <PH>[Vorname Name, Funktion]</PH>
    </P>

    <H2>Haftungsausschluss</H2>
    <P>
      Trotz sorgfältiger inhaltlicher Kontrolle übernimmt NIVARA keine Haftung für die Richtigkeit,
      Vollständigkeit und Aktualität der Inhalte. Haftungsansprüche, welche sich auf Schäden
      materieller oder immaterieller Art beziehen, die durch die Nutzung der dargebotenen
      Informationen entstehen, sind ausgeschlossen, sofern kein nachweislich vorsätzliches oder
      grobfahrlässiges Verschulden vorliegt.
    </P>

    <H2>Externe Links</H2>
    <P>
      Diese Website enthält Links zu externen Websites Dritter (z. B. Google Maps, WhatsApp). Auf
      deren Inhalte hat NIVARA keinen Einfluss; für sie ist stets der jeweilige Anbieter verantwortlich.
    </P>

    <H2>Urheberrecht</H2>
    <P>
      Sämtliche Inhalte dieser Website (Texte, Bilder, Logo, Tarife) sind urheberrechtlich
      geschützt. Eine Verwendung, Vervielfältigung oder Veröffentlichung ohne schriftliche
      Zustimmung von NIVARA ist nicht gestattet.
    </P>

    <H2>Gestaltung &amp; Technik</H2>
    <P>
      Web-Plattform: <a href="https://nivaragroup.ch" className="text-[var(--rs-primary)] underline">nivaragroup.ch</a><br />
      Technische Umsetzung: NIVARA Group
    </P>
  </LegalLayout>
);
